import { useMemo, useState } from 'react';
import { useAuthStore } from '../../stores/auth';
import {
  Alert,
  BodyText,
  Button,
  Card,
  Heading,
  Input,
  PageHeader,
  Select,
  Tabs,
} from '@aaimplatform/ui';
import type { ColumnDef } from '@aaimplatform/ui';
import type {
  components as AuditComponents,
  operations as AuditOperations,
} from '@aaimplatform/institutional-types/ucc-filing-audit-compliance';
import type { components as BulkComponents } from '@aaimplatform/institutional-types/ucc-bulk-operations';
import {
  RefreshCw as RefreshIcon,
  FileText as ReportIcon,
} from 'lucide-react';
import {
  useAuditEvents,
  useGenerateComplianceReport,
  useBulkOperations,
} from '../../hooks';
import { formatCount, formatDate, formatDateTime } from '../../utils/display-format';
import {
  AUDIT_ACTOR_TYPE_LABEL,
  AUDIT_EVENT_TYPE_LABEL,
  AUDIT_EVENT_TYPE_VARIANT,
  BULK_OPERATION_STATUS_LABEL,
  BULK_OPERATION_STATUS_VARIANT,
  BULK_OPERATION_TYPE_LABEL,
  COMPLIANCE_REPORT_TYPE_LABEL,
  CodeBadge,
  FILING_STATUS_LABEL,
  labelFor,
  optionsFrom,
  shortId,
  UccTable,
  UccTenantScope,
  type ComplianceReportType,
} from './ucc-display';

type FilingAuditEvent = AuditComponents['schemas']['FilingAuditEvent'];
type ComplianceReport = AuditComponents['schemas']['ComplianceReport'];
type BulkOperation = BulkComponents['schemas']['BulkOperation'];

const TAB_ITEMS = [
  { id: 'audit', label: 'Audit Trail' },
  { id: 'reports', label: 'Reports' },
  { id: 'bulk', label: 'Bulk Operations' },
];

const REPORT_TYPE_OPTIONS = optionsFrom(COMPLIANCE_REPORT_TYPE_LABEL);

/** Default reporting period: the 90 days ending today, as yyyy-mm-dd for the date inputs. */
function defaultPeriod(): { start: string; end: string } {
  const end = new Date();
  const start = new Date(end.getTime() - 90 * 24 * 60 * 60 * 1000);
  return { start: start.toISOString().slice(0, 10), end: end.toISOString().slice(0, 10) };
}

/**
 * POST /compliance-reports/generate body per
 * schema-registry/ucc-filing-audit-compliance.openapi.yaml: a ComplianceReportType
 * and a required period as date-times. The page sent `filing_compliance`,
 * which the enum does not contain, and no period, so every request failed.
 * The period covers whole UTC days: start at 00:00:00Z, end at 23:59:59Z.
 */
export function buildComplianceReportRequest(input: {
  tenantId: string;
  reportType: ComplianceReportType;
  periodStartDate: string;
  periodEndDate: string;
}): AuditOperations['generateComplianceReport']['requestBody']['content']['application/json'] {
  return {
    tenantId: input.tenantId,
    reportType: input.reportType,
    periodStart: `${input.periodStartDate}T00:00:00Z`,
    periodEnd: `${input.periodEndDate}T23:59:59Z`,
  };
}

/**
 * A filing state from an audit event. The spec types previousState and
 * newState as free strings; a FilingStatus value prints its label, and any
 * other value prints as recorded.
 */
function stateLabel(state: string): string {
  return Object.prototype.hasOwnProperty.call(FILING_STATUS_LABEL, state) ? labelFor(FILING_STATUS_LABEL, state) : state;
}

/** "Submitted to Acknowledged" from an audit event's state pair, or the absent glyph. */
export function stateChange(event: Pick<FilingAuditEvent, 'previousState' | 'newState'>): string {
  const from = event.previousState ? stateLabel(event.previousState) : null;
  const to = event.newState ? stateLabel(event.newState) : null;
  if (from && to) return `${from} to ${to}`;
  if (to) return `now ${to}`;
  if (from) return `was ${from}`;
  return '--';
}

function useAuditColumns(): ColumnDef<FilingAuditEvent, unknown>[] {
  return useMemo<ColumnDef<FilingAuditEvent, unknown>[]>(
    () => [
      {
        accessorKey: 'timestamp',
        header: 'Timestamp',
        cell: ({ row }) => <span className="text-sm text-secondary">{formatDateTime(row.original.timestamp)}</span>,
      },
      {
        accessorKey: 'eventType',
        header: 'Event',
        cell: ({ row }) => (
          <CodeBadge code={row.original.eventType} labels={AUDIT_EVENT_TYPE_LABEL} variants={AUDIT_EVENT_TYPE_VARIANT} />
        ),
      },
      {
        accessorKey: 'filingId',
        header: 'Filing',
        cell: ({ row }) => <span className="font-mono text-sm">{shortId(row.original.filingId)}</span>,
      },
      {
        accessorKey: 'actorType',
        header: 'Actor',
        cell: ({ row }) => (
          <div>
            <div className="text-sm text-primary">{labelFor(AUDIT_ACTOR_TYPE_LABEL, row.original.actorType)}</div>
            <div className="text-xs font-mono text-tertiary">{row.original.actorId}</div>
          </div>
        ),
      },
      {
        id: 'change',
        header: 'State Change',
        cell: ({ row }) => <span className="text-sm text-secondary">{stateChange(row.original)}</span>,
      },
    ],
    [],
  );
}

function useBulkColumns(): ColumnDef<BulkOperation, unknown>[] {
  return useMemo<ColumnDef<BulkOperation, unknown>[]>(
    () => [
      {
        accessorKey: 'batchId',
        header: 'Batch ID',
        cell: ({ row }) => <span className="font-mono text-sm">{shortId(row.original.batchId)}</span>,
      },
      {
        accessorKey: 'operationType',
        header: 'Operation',
        cell: ({ row }) => <span className="text-sm">{labelFor(BULK_OPERATION_TYPE_LABEL, row.original.operationType)}</span>,
      },
      {
        accessorKey: 'status',
        header: 'Status',
        cell: ({ row }) => (
          <CodeBadge code={row.original.status} labels={BULK_OPERATION_STATUS_LABEL} variants={BULK_OPERATION_STATUS_VARIANT} />
        ),
      },
      {
        id: 'progress',
        header: 'Progress',
        cell: ({ row }) => (
          <span className="text-sm tabular-nums">
            {formatCount(row.original.processedItems)} of {formatCount(row.original.totalItems)}
          </span>
        ),
      },
      {
        accessorKey: 'createdAt',
        header: 'Created',
        cell: ({ row }) => <span className="text-sm text-secondary">{formatDate(row.original.createdAt)}</span>,
      },
    ],
    [],
  );
}

export function UCCCompliancePage() {
  const tenantId = useAuthStore(s => s.user?.tenantId) ?? '';
  const [activeTab, setActiveTab] = useState('audit');
  const [reportType, setReportType] = useState<ComplianceReportType>('filing_coverage');
  const [period, setPeriod] = useState(defaultPeriod);
  const [generatedReports, setGeneratedReports] = useState<ComplianceReport[]>([]);

  const audit = useAuditEvents({ tenantId });
  const bulk = useBulkOperations({ tenantId });
  const generateReport = useGenerateComplianceReport();
  const auditColumns = useAuditColumns();
  const bulkColumns = useBulkColumns();

  const activeQuery = activeTab === 'bulk' ? bulk : audit;
  const periodValid = Boolean(period.start && period.end && period.start <= period.end);

  const handleGenerateReport = async () => {
    if (!periodValid) return;
    try {
      const report = await generateReport.mutateAsync(
        buildComplianceReportRequest({
          tenantId,
          reportType,
          periodStartDate: period.start,
          periodEndDate: period.end,
        }),
      );
      setGeneratedReports((prev) => [report, ...prev]);
    } catch {
      // Rendered from generateReport.error.
    }
  };

  return (
    <div>
      <PageHeader
        description="Audit trail, compliance reports, and bulk operations"
        actions={
          activeTab !== 'reports' ? (
            <Button
              variant="secondary"
              onClick={() => activeQuery.refetch()}
              disabled={activeQuery.isFetching || !tenantId}
            >
              <RefreshIcon className={`mr-2 ${activeQuery.isFetching ? 'animate-spin' : ''}`} size={16} />
              Refresh
            </Button>
          ) : undefined
        }
      />

      <UccTenantScope tenantId={tenantId} subject="lists" />

      {tenantId && (
        <Tabs
          tabs={TAB_ITEMS}
          activeTab={activeTab}
          onTabChange={setActiveTab}
          aria-label="UCC records"
          panelClassName="mt-4"
        >
          {activeTab === 'audit' && (
            <UccTable<FilingAuditEvent>
              columns={auditColumns}
              rows={audit.data?.data ?? []}
              isLoading={audit.isLoading}
              error={audit.error}
              errorTitle="Could not load the audit trail"
              onRetry={() => audit.refetch()}
              empty={{
                title: 'No audit events for this tenant yet',
                description:
                  "Events are recorded when one of this tenant's filings is created, submitted, acknowledged, amended, continued or terminated.",
              }}
              getRowId={(row) => row.eventId}
            />
          )}

          {activeTab === 'reports' && (
            <Card className="space-y-6">
              <div>
                <Heading level={3} size="card" className="mb-2">Generate a Compliance Report</Heading>
                <BodyText variant="ui" className="text-sm text-tertiary">
                  Choose a report type and a reporting period. The report is stored as a point-in-time snapshot and
                  returned with its report ID.
                </BodyText>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
                <Select
                  label="Report Type"
                  options={REPORT_TYPE_OPTIONS}
                  value={reportType}
                  onChange={(e) => setReportType(e.target.value as ComplianceReportType)}
                  required
                />
                <Input
                  label="Period Start"
                  type="date"
                  value={period.start}
                  onChange={(e) => setPeriod((prev) => ({ ...prev, start: e.target.value }))}
                  required
                />
                <Input
                  label="Period End"
                  type="date"
                  value={period.end}
                  onChange={(e) => setPeriod((prev) => ({ ...prev, end: e.target.value }))}
                  error={period.start && period.end && period.start > period.end ? 'The end date is before the start date' : undefined}
                  required
                />
                <Button variant="primary" onClick={handleGenerateReport} disabled={generateReport.isPending || !periodValid}>
                  <ReportIcon className="mr-2" size={16} />
                  {generateReport.isPending ? 'Generating...' : 'Generate Report'}
                </Button>
              </div>

              {generateReport.error && (
                <Alert type="error" title="The report was not generated">
                  {generateReport.error.message}
                </Alert>
              )}

              <div>
                <Heading level={3} size="card" className="mb-2">Reports Generated This Session</Heading>
                {generatedReports.length === 0 ? (
                  <BodyText variant="ui" className="text-sm text-tertiary">
                    No reports generated yet in this session. The compliance service returns each report by ID and
                    offers no list of earlier reports.
                  </BodyText>
                ) : (
                  <ul className="space-y-2">
                    {generatedReports.map((report) => (
                      <li key={report.reportId} className="text-sm font-sans text-primary">
                        <span className="font-medium">{labelFor(COMPLIANCE_REPORT_TYPE_LABEL, report.reportType)}</span>
                        {' '}for {formatDate(report.periodStart)} to {formatDate(report.periodEnd)}, generated{' '}
                        {formatDateTime(report.generatedAt)}. Report ID{' '}
                        <span className="font-mono">{report.reportId}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </Card>
          )}

          {activeTab === 'bulk' && (
            <UccTable<BulkOperation>
              columns={bulkColumns}
              rows={bulk.data?.data ?? []}
              isLoading={bulk.isLoading}
              error={bulk.error}
              errorTitle="Could not load bulk operations"
              onRetry={() => bulk.refetch()}
              empty={{
                title: 'No bulk operations for this tenant yet',
                description: 'Bulk continuations, amendments, terminations and imports appear here once started.',
              }}
              getRowId={(row) => row.batchId}
            />
          )}
        </Tabs>
      )}
    </div>
  );
}
